/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Home/AdminDashboardHome.tsx.
 */

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../api/axiosClient";
import publicAxiosClient from "../../../../api/publicAxiosClient";
import { API_URLS } from "../../../../constants/apiUrls";
import StatCard from "../../../../components/common/StatCard";
import MaterialCard from "../../../../components/common/MaterialCard";
import { pickMaterialImagePath } from "../../../../utils/resolveUploadAssetUrl";
import { isAdminQueueReviewStatus } from "../../../../constants/reviewStatus";
import "./AdminDashboardHome.css";

interface InsightMetric {
  value: number | null;
  unit?: string | null;
}

interface MaterialInsight {
  id: number;
  name: string;
  code: string;
  description?: string | null;
  image?: string | null;
  imageUrl?: string | null;
  image_url?: string | null;
  allDatasets?: InsightMetric | null;
  sampleSize?: InsightMetric | null;
  average?: InsightMetric | null;
  min?: InsightMetric | null;
  max?: InsightMetric | null;
  standardDeviation?: InsightMetric | null;
}

interface DashboardStats {
  totalMaterials: number;
  totalSubmissions: number;
  totalUsers: number;
  totalExperts: number;
  pendingReviews: number;
}

interface AdminDashboardHomeProps {
  onExploreMaterial?: (materialId: number) => void;
}

const AdminDashboardHome: React.FC<AdminDashboardHomeProps> = ({ onExploreMaterial }) => {
  const [materials, setMaterials] = useState<MaterialInsight[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalMaterials: 0,
    totalSubmissions: 0,
    totalUsers: 0,
    totalExperts: 0,
    pendingReviews: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [insightsRes, catalogRes] = await Promise.all([
          axiosClient.get("/admin/material-insights"),
          axiosClient.get(API_URLS.MATERIALS.LIST).catch(() =>
            publicAxiosClient.get(API_URLS.MATERIALS.LIST).catch(() => ({
              data: { data: [] },
            }))
          ),
        ]);

        const insightsData: MaterialInsight[] = Array.isArray(insightsRes.data?.data)
          ? insightsRes.data.data
          : Array.isArray(insightsRes.data)
            ? insightsRes.data
            : [];

        const catalogItems = Array.isArray(catalogRes.data?.data)
          ? catalogRes.data.data
          : Array.isArray(catalogRes.data)
            ? catalogRes.data
            : [];

        const catalogImageById = new Map<number, string>();
        for (const item of catalogItems) {
          const id = Number(item?.id);
          const path = pickMaterialImagePath(item);
          if (Number.isFinite(id) && path) {
            catalogImageById.set(id, path);
          }
        }

        const insightsWithImages = insightsData.map((material) => ({
          ...material,
          imageUrl:
            pickMaterialImagePath(material) ||
            catalogImageById.get(Number(material.id)) ||
            null,
        }));

        setMaterials(insightsWithImages);

        // Fetch statistics
        try {
          const [submissionsRes, usersRes, expertsRes] = await Promise.all([
            axiosClient.get("/admin/submissions").catch(() => ({ data: { success: false, data: [] } })),
            axiosClient.get("/admin/users").catch(() => ({ data: [] })),
            axiosClient.get("/admin/experts").catch(() => ({ data: { success: false, data: [] } })),
          ]);

          const submissions = submissionsRes.data?.success
            ? submissionsRes.data.data || []
            : Array.isArray(submissionsRes.data)
            ? submissionsRes.data
            : [];

          const users = Array.isArray(usersRes.data)
            ? usersRes.data
            : Array.isArray(usersRes.data?.data)
            ? usersRes.data.data
            : [];

          const experts = expertsRes.data?.success
            ? expertsRes.data.data || []
            : Array.isArray(expertsRes.data)
            ? expertsRes.data
            : [];

          const pendingReviews = submissions.filter((s: any) =>
            isAdminQueueReviewStatus(s.reviewStatus)
          ).length;

          setStats({
            totalMaterials: insightsWithImages.length,
            totalSubmissions: submissions.length,
            totalUsers: users.length,
            totalExperts: experts.length,
            pendingReviews,
          });
        } catch (statsError) {
          console.error("Failed to fetch stats:", statsError);
          // Set defaults if stats fail
          setStats({
            totalMaterials: insightsWithImages.length,
            totalSubmissions: 0,
            totalUsers: 0,
            totalExperts: 0,
            pendingReviews: 0,
          });
        }
      } catch (error) {
        console.error("❌ Failed to fetch materials:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="background-form-container analysis-shell">
        <div className="analysis-banner">
          <span className="analysis-banner__title">Loading Dashboard...</span>
        </div>
        <div className="loading-placeholder">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="background-form-container analysis-shell">
        <div className="error-placeholder">
          <div className="error-icon">⚠️</div>
          <h3>Unable to load dashboard</h3>
          <p>Please check your connection or try again later.</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="background-form-container analysis-shell">
      {/* Banner Section */}
      <div className="analysis-banner">
        <span className="analysis-banner__title">
          Admin Dashboard Overview
        </span>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          value={stats.totalSubmissions}
          label="Total Submissions"
        />
        <StatCard
          value={stats.totalUsers}
          label="Total Users"
        />
        <StatCard
          value={stats.totalExperts}
          label="Total Experts"
        />
        <StatCard
          value={stats.pendingReviews}
          label="Pending Reviews"
        
        />
      </div>

      {/* Materials Section */}
      <div className="materials-section">
        <div className="section-header">
          <h2 className="section-title">Material Insights</h2>
          <p className="section-subtitle">Explore datasets and statistics for each material</p>
        </div>

        {materials.length === 0 ? (
          <div className="empty-materials">
            <div className="empty-icon">📦</div>
            <h3>No Materials Available</h3>
            <p>Materials will appear here once they are added to the system.</p>
          </div>
        ) : (
          <div className="material-list">
            {materials.map((material) => (
              <MaterialCard
                key={material.id}
                id={material.id}
                name={material.name}
                code={material.code}
                imageUrl={material.imageUrl || pickMaterialImagePath(material) || undefined}
                apiBaseUrl={axiosClient.defaults.baseURL}
                stats={{
                  allDatasets: material.allDatasets?.value ?? undefined,
                  sampleSize: material.sampleSize?.value ?? undefined,
                  average: material.average?.value ?? undefined,
                  min: material.min?.value ?? undefined,
                  max: material.max?.value ?? undefined,
                  standardDeviation: material.standardDeviation?.value ?? undefined,
                }}
                onExplore={(id) => {
                  onExploreMaterial?.(id);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/** React component — Admin Dashboard Home. */
export default AdminDashboardHome;
